import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import debug from "@plannel/utils/debug-util";

const ITEM_CLASS_BYPRODUCT = "FP_BYPRODUCT";

/**
 * 공정(Route) 조회 팝업 — old `RoutePopup.jsx` 대응. old와 동일하게 **두 type 모두 같은
 * 소스(`factoryplan/producingroutes` — 공정이 생산하는 품목까지 조인된 목록)**를 쓰고,
 * old와 동일한 `type` prop 이름/값으로 분기한다(old `props.type`, 기본값도 old와 동일하게 'routeCd').
 * - type="routeCd"(기본): routeCd=""로 전량 조회, 더블클릭 시 선택한 공정이 생산하는 품목
 *   전체를 집계해서 반환(old `onCellDblClicked`의 `props.type === 'routeCd'` 분기 대응)
 * - type="routeProdItems": 특정 공정(routeCd)의 생산 품목 목록 조회 전용(선택 없음).
 *   BorTab.js의 routeProdItems 컬럼 조회 버튼이 이 type으로 연다.
 *
 * props: open, onClose(), planScope, onSelect(row) — type="routeCd"에서만 사용,
 *   type?("routeCd"|"routeProdItems"), routeCd? — routeProdItems에서 조회 대상 공정 코드
 *
 * TODO(MIG-T2): [common/popup/RoutePopup] old의 RealGrid mergeRule(같은 routeCd를 가진 행들의
 *   routeCd/routeNm/routeDescTxt 셀을 시각적으로 병합) 미이관.
 *   old: `routeGroup` 하위 컬럼에 `mergeRule: { criteria: "value" }`/`"prevvalues + value"`.
 *   사유: 셀 병합은 CLAUDE.md Tier2 항목 — 데이터/선택 동작은 동일, 시각적 병합만 유보.
 */
const RoutePopup = ({ t, open, onClose, planScope, onSelect, type = "routeCd", routeCd }) => {
  const isProdItemsMode = type === "routeProdItems";
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t(isProdItemsMode ? "FP_ROUTE_PROD_ITEM" : "FP_ROUTE_SELECT"), viewName: "" }), [t, isProdItemsMode]);

  // old routePopupGridItems 대응 — routeGroup(코드/명칭/설명, 항상 표시)+prodItemGroup(재고/품목,
  // 항상 표시). 두 모드 모두 동일 컬럼 구성(old도 type과 무관하게 같은 그리드 정의 하나만 씀).
  // itemClassCd는 old setGridComboList(FP_ITEM_CLASS_CD) + FP_BYPRODUCT 특수 라벨 대응.
  const columnDefs = useMemo(() => ([
    {
      headerName: t("FP_ROUTE"), groupId: "route", marryChildren: true, openByDefault: true,
      children: [
        { headerName: t("FP_ROUTE_CD"), field: "routeCd", colId: "routeCd", width: 150, editable: false },
        { headerName: t("FP_ROUTE_NM"), field: "routeNm", colId: "routeNm", width: 200, editable: false },
        { headerName: t("FP_DESC_TXT"), field: "routeDescTxt", colId: "routeDescTxt", width: 130, editable: false }
      ]
    },
    {
      headerName: t("FP_PRODUCTION_ITEM"), groupId: "prodItem", marryChildren: true, openByDefault: true,
      children: [
        { headerName: t("FP_INVENTORY_CD"), field: "inventoryCd", colId: "inventoryCd", width: 150, editable: false },
        { headerName: t("FP_INVENTORY_NAME"), field: "inventoryNm", colId: "inventoryNm", width: 200, editable: false },
        { headerName: t("FP_ITEM_CD"), field: "itemCd", colId: "itemCd", width: 150, editable: false },
        { headerName: t("FP_ITEM_NM"), field: "itemNm", colId: "itemNm", width: 200, editable: false },
        { headerName: t("FP_ITEM_CLASS_CD"), field: "itemClassCd", colId: "itemClassCd", width: 110, type: ["centerAligned"], editable: false,
          valueFormatter: p => {
            if (p.value === "FP_BYPRODUCT") return t(ITEM_CLASS_BYPRODUCT);
            const key = `FP_ITEM_CLASS_CD_${p.value}`;
            const label = t(key);
            return label !== key ? label : p.value;
          } }
      ]
    }
  ]), [t]);

  // old loadData 대응 — 두 type 모두 같은 엔드포인트, routeCd만 다르게(routeCd="" 전량, routeProdItems=지정값).
  const loadProducingRoutes = () => {
    if (!planScope) return;
    fpCommonService.getProducingRoutes({ planScope, routeCd: isProdItemsMode ? routeCd : "", bomTpCd: "P" })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); });
  };

  useEffect(() => {
    if (!open || !planScope) return;
    if (isProdItemsMode && !routeCd) return;
    loadProducingRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope, type, routeCd]);

  // old onCellDblClicked(`props.type === 'routeCd'`) 대응 — 클릭한 행과 같은 routeCd를 가진 모든
  // 행(같은 공정이 생산하는 품목 전체)을 모아 routeProdItems(품목 코드 join)를 만들고, 그중
  // 첫 비부산물(FP_BYPRODUCT가 아닌) 품목을 "대표 품목"으로 반환한다.
  const onRowDoubleClicked = (e) => {
    if (isProdItemsMode || !e.data) return;

    const clickedRouteCd = e.data.routeCd;
    const routeRows = rowData.filter(row => row.routeCd === clickedRouteCd);
    const routeProdItems = routeRows.map(row => row.inventoryCd).join(", ");
    const mainItem = routeRows.find(row => row.itemClassCd !== "FP_BYPRODUCT");

    onSelect({
      routeCd: clickedRouteCd,
      routeNm: e.data.routeNm,
      routeProdItems,
      inventoryCd: mainItem.inventoryCd,
      inventoryNm: mainItem.inventoryNm,
      itemCd: mainItem.itemCd,
      itemNm: mainItem.itemNm,
      itemClassCd: mainItem.itemClassCd
    });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t(isProdItemsMode ? "FP_ROUTE_PROD_ITEM" : "FP_ROUTE_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            rowSelection={isProdItemsMode ? undefined : { mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(RoutePopup);
